import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle, Edit3, XCircle } from 'lucide-react';

import {
  ClearSelectionButton,
  DateInfoBox,
  SelectedTimeSummary,
  TimeSlotItem,
  TimeSlotLegend,
} from '../components/helpers/TimeSlotHelpers';
import { usePaymentAccess } from '../../../../../hooks/usePaymentAccess';
import { MinimumDurationPopup } from '../components/modals/MinimumDurationPopup';
import { ProductInfoModal } from '../components/modals/ProductInfoModal';
import { ProductHeader } from '../components/sections/ProductHeader';
import PaymentAccessModal from '../components/modals/PaymentAccessModal';

const normaliseTime = (raw) => {
  if (!raw) return null;
  if (raw.length > 8 && raw.includes('T')) return raw.substring(11, 16);
  return raw.substring(0, 5);
};

const formatTime12Hour = (time24) => {
  if (!time24) return '--:-- --';
  try {
    const [hour, min] = time24.substring(0, 5).split(':').map(Number);
    if (isNaN(hour) || isNaN(min)) return '--:-- --';
    const period = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${min.toString().padStart(2, '0')} ${period}`;
  } catch {
    return '--:-- --';
  }
};

const formatTimeRange = (start, end) => {
  if (!start || !end) return '--:-- --';
  try {
    const [sh, sm] = start.substring(0, 5).split(':').map(Number);
    const [eh, em] = end.substring(0, 5).split(':').map(Number);
    if (isNaN(sh) || isNaN(eh)) return '--:-- --';
    const sp = sh >= 12 ? 'PM' : 'AM';
    const ep = eh >= 12 ? 'PM' : 'AM';
    const s12 = sh % 12 || 12;
    const e12 = eh % 12 || 12;
    const smStr = sm !== 0 ? `:${sm.toString().padStart(2, '0')}` : '';
    const emStr = em !== 0 ? `:${em.toString().padStart(2, '0')}` : '';
    if (sp === ep) return `${s12}${smStr} – ${e12}${emStr} ${ep}`;
    return `${s12}${smStr} ${sp} – ${e12}${emStr} ${ep}`;
  } catch {
    return '--:-- --';
  }
};

const RecurrenceProductResultCard = ({
  product,
  onSelect,
  searchCriteria,
  selectedTime,
  isExpanded = true,
  occurrenceNumber,
  totalOccurrences,
  isAvailable = true,
  availabilityInfo = null,
  isReviewMode = false,
  onEdit,
  onDelete,
  companyTimezone,
  onClearSelection,
}) => {
  const [isCardExpanded, setIsCardExpanded]       = useState(isExpanded);
  const [selectedSlots, setSelectedSlots]         = useState([]);
  const [isDragging, setIsDragging]               = useState(false);
  const [dragStart, setDragStart]                 = useState(null);
  const [hoveredSlot, setHoveredSlot]             = useState(null);
  const [showMinimumInfo, setShowMinimumInfo]     = useState(false);
  const [showProductInfo, setShowProductInfo]     = useState(false);
  const [showPaymentModal, setShowPaymentModal]   = useState(false);
  const [timeSlots, setTimeSlots]                 = useState([]);
  const [isEditing, setIsEditing]                 = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);
  const [showClearMessage, setShowClearMessage]   = useState(false);
  const [bookingError, setBookingError]           = useState('');
  const [isDayOff, setIsDayOff]                   = useState(false);
  const [hasUserSelected, setHasUserSelected]     = useState(false);

  const timelineRef    = useRef(null);
  const touchDragging  = useRef(false);
  const touchDragStart = useRef(null);
  const clearMessageTimeout = useRef(null);

  const companyState                    = useSelector((s) => s?.companyReducer?.company) || {};
  const { isPaymentAllowed, contactPerson } = usePaymentAccess();

  const timezone =
    companyTimezone ||
    companyState?.operatingLocation?.zoneId ||
    'America/New_York';

  const openTime             = companyState?.workingSchedule?.openHours           || '08:00';
  const closeTime            = companyState?.workingSchedule?.closeHours          || '18:00';
  const slotIntervalInMinutes = companyState?.workingSchedule?.slotIntervalInMinutes || 60;
  const workingDays = useMemo(
    () => companyState?.workingSchedule?.workingDays || [],
    [companyState?.workingSchedule?.workingDays],
  );
  const holidays = useMemo(
    () => companyState?.workingSchedule?.holidays || [],
    [companyState?.workingSchedule?.holidays],
  );

  const nowInTz = useCallback(
    () => moment.tz(timezone),
    [timezone],
  );

  const dateInTz = useCallback(
    (dateStr) => moment.tz(dateStr, 'YYYY-MM-DD', timezone),
    [timezone],
  );

  const isDateAvailableForBooking = useCallback(() => {
    if (!searchCriteria?.date) return true;

    const dateMom = dateInTz(searchCriteria.date);

    const isHoliday = holidays.some((h) => {
      const hDate = typeof h === 'string' ? h : h?.date;
      return hDate && moment(hDate).isSame(dateMom, 'day');
    });
    if (isHoliday) return false;

    if (workingDays.length > 0) {
      const dayName = dateMom.format('dddd').toUpperCase();
      if (!workingDays.includes(dayName)) return false;
    }

    return true;
  }, [searchCriteria?.date, holidays, workingDays, dateInTz]);

  const isPastTimeSlot = useCallback(
    (timeStr) => {
      try {
        const now      = nowInTz();
        const selDate  = dateInTz(searchCriteria.date);
        if (!selDate.isSame(now, 'day')) return false;

        const [h, m] = timeStr.substring(0, 5).split(':').map(Number);
        if (isNaN(h) || isNaN(m)) return false;

        return selDate.clone().hour(h).minute(m).second(0).isBefore(now);
      } catch {
        return false;
      }
    },
    [nowInTz, dateInTz, searchCriteria?.date],
  );

  const generateTimeSlots = useCallback(() => {
    const slots = [];

    const [oh, om] = openTime.split(':').map(Number);
    const [ch, cm] = closeTime.split(':').map(Number);
    const openMin  = oh * 60 + om;
    const closeMin = ch * 60 + cm;

    const bookedMap = new Map();
    if (Array.isArray(product?.slots)) {
      product.slots.forEach((apiSlot) => {
        if (apiSlot.status === 'RESERVED' || apiSlot.status === 'BOOKED') {
          const st = normaliseTime(apiSlot.startTime);
          const et = normaliseTime(apiSlot.endTime);
          if (!st || !et) return;
          const [sHr, sMin] = st.split(':').map(Number);
          const [eHr, eMin] = et.split(':').map(Number);
          for (let m = sHr * 60 + sMin; m < eHr * 60 + eMin; m += slotIntervalInMinutes) {
            const key = `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
            bookedMap.set(key, apiSlot.status);
          }
        }
      });
    }

    const conflictMap = new Map();
    if (Array.isArray(availabilityInfo?.conflictingSlots)) {
      availabilityInfo.conflictingSlots.forEach((c) => {
        const st = normaliseTime(c.start);
        const et = normaliseTime(c.end);
        if (!st || !et) return;
        const [sHr, sMin] = st.split(':').map(Number);
        const [eHr, eMin] = et.split(':').map(Number);
        for (let m = sHr * 60 + sMin; m < eHr * 60 + eMin; m += slotIntervalInMinutes) {
          const key = `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
          conflictMap.set(key, 'CONFLICT');
        }
      });
    }

    let selStartMin = -1;
    let selEndMin   = -1;
    if (selectedTime?.start && selectedTime?.end && !hasUserSelected) {
      const ss = normaliseTime(selectedTime.start);
      const se = normaliseTime(selectedTime.end);
      if (ss && se) {
        const [ssh, ssm] = ss.split(':').map(Number);
        const [seh, sem] = se.split(':').map(Number);
        if (!isNaN(ssh) && !isNaN(seh)) {
          selStartMin = ssh * 60 + ssm;
          selEndMin   = seh * 60 + sem;
        }
      }
    }

    for (let t = openMin; t < closeMin; t += slotIntervalInMinutes) {
      const hour   = Math.floor(t / 60);
      const minute = t % 60;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      const nt = t + slotIntervalInMinutes;
      const nextStr = `${Math.floor(nt / 60).toString().padStart(2, '0')}:${(nt % 60).toString().padStart(2, '0')}`;

      const status     = bookedMap.get(timeStr);
      const isBooked   = status === 'RESERVED' || status === 'BOOKED';
      const isConflict = conflictMap.has(timeStr);
      const isPast     = isPastTimeSlot(timeStr);
      const isSelected = selStartMin >= 0 && t >= selStartMin && t < selEndMin;

      slots.push({
        time:         timeStr,
        endTime:      nextStr,
        displayLabel: formatTimeRange(timeStr, nextStr),
        rangeLabel:   formatTimeRange(timeStr, nextStr),
        booked:       isBooked,
        conflict:     isConflict,
        past:         isPast,
        status:       status || 'OPEN',
        selectable:   !isBooked && !isPast && !isConflict,
        selected:     isSelected,
      });
    }

    return slots;
  }, [
    openTime, closeTime, slotIntervalInMinutes,
    product?.slots, availabilityInfo?.conflictingSlots,
    selectedTime, isPastTimeSlot, hasUserSelected,
  ]);

  useEffect(() => {
    const available = isDateAvailableForBooking();
    setIsDayOff(!available);
    setTimeSlots(generateTimeSlots());
    
    // Only auto-select if there's a selectedTime and user hasn't manually interacted
    if (selectedTime?.start && selectedTime?.end && !hasUserSelected) {
      // Keep selected slots based on selectedTime
      const slotsWithSelection = generateTimeSlots();
      setSelectedSlots(slotsWithSelection.filter((s) => s.selected));
    } else {
      setSelectedSlots([]);
    }
    
    setBookingError('');
    setShowMinimumInfo(false);
    if (clearMessageTimeout.current) {
      clearTimeout(clearMessageTimeout.current);
    }
    setShowClearMessage(false);
  }, [
    product?.slots, product?.primaryId,
    companyState?.workingSchedule,
    searchCriteria?.date,
    timezone,
    selectedTime,
    availabilityInfo,
    generateTimeSlots,
    isDateAvailableForBooking,
    hasUserSelected,
  ]);

  useEffect(() => {
    setSelectedSlots(timeSlots.filter((s) => s.selected));
  }, [timeSlots]);

  const validateMinimum = (slots) => {
    const totalMinutes = slots.length * slotIntervalInMinutes;
    const minMinutes = 60;
    if (totalMinutes < minMinutes) {
      const requiredSlots = Math.ceil(minMinutes / slotIntervalInMinutes);
      return { isValid: false, requiredSlots };
    }
    return { isValid: true, requiredSlots: 0 };
  };

  const updateSelectedSlots = (slots) => {
    const selected = slots.filter((s) => s.selected);
    setSelectedSlots(selected);
    setHasUserSelected(true); // Mark that user has manually selected

    if (selected.length > 0) {
      const v = validateMinimum(selected);
      setShowMinimumInfo(!v.isValid);
      setBookingError(
        v.isValid
          ? ''
          : `Minimum booking is 1 hour. Select at least ${v.requiredSlots} slot(s).`,
      );
    } else {
      setShowMinimumInfo(false);
      setBookingError('');
    }
  };

  const applyDragRange = useCallback((startIdx, endIdx, slots) => {
    const lo = Math.min(startIdx, endIdx);
    const hi = Math.max(startIdx, endIdx);
    let validEnd = lo;
    for (let i = lo; i <= hi; i++) {
      if (!slots[i].selectable) break;
      validEnd = i;
    }
    return slots.map((s, i) => ({ ...s, selected: i >= lo && i <= validEnd && s.selectable }));
  }, []);

  const handleMouseDown = (index) => {
    if (!isEditing || !timeSlots[index]?.selectable) return;
    setIsDragging(true);
    setDragStart(index);
    const updated = timeSlots.map((s, i) => ({ ...s, selected: i === index && s.selectable }));
    setTimeSlots(updated);
    updateSelectedSlots(updated);
  };

  const handleMouseEnter = (index) => {
    setHoveredSlot(index);
    if (!isEditing || !isDragging) return;
    const updated = applyDragRange(dragStart, index, timeSlots);
    setTimeSlots(updated);
    updateSelectedSlots(updated);
  };

  const handleMouseLeave = () => setHoveredSlot(null);
  const handleMouseUp    = () => { setIsDragging(false); setDragStart(null); };

  const indexFromPoint = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return -1;
    const wrapper = el.closest('[data-slot-index]');
    if (!wrapper) return -1;
    return parseInt(wrapper.getAttribute('data-slot-index'), 10);
  };

  const onTouchStart = (e) => {
    if (!isEditing || e.touches.length !== 1) return;
    const { clientX, clientY } = e.touches[0];
    const index = indexFromPoint(clientX, clientY);
    if (index < 0 || !timeSlots[index]?.selectable) return;
    e.preventDefault();
    touchDragging.current  = true;
    touchDragStart.current = index;
    setIsDragging(true);
    setDragStart(index);
    const updated = timeSlots.map((s, i) => ({ ...s, selected: i === index && s.selectable }));
    setTimeSlots(updated);
    updateSelectedSlots(updated);
  };

  const onTouchMove = (e) => {
    if (!isEditing || !touchDragging.current || e.touches.length !== 1) return;
    e.preventDefault();
    const { clientX, clientY } = e.touches[0];
    const index = indexFromPoint(clientX, clientY);
    if (index < 0) return;
    setHoveredSlot(index);
    const updated = applyDragRange(touchDragStart.current, index, timeSlots);
    setTimeSlots(updated);
    updateSelectedSlots(updated);
  };

  const onTouchEnd = () => {
    touchDragging.current  = false;
    touchDragStart.current = null;
    setIsDragging(false);
    setDragStart(null);
    setHoveredSlot(null);
  };

  const handleMobileTap = (index) => {
    if (!isEditing || !timeSlots[index]?.selectable) return;

    if (selectedSlots.length === 0) {
      const updated = timeSlots.map((s, i) => ({ ...s, selected: i === index && s.selectable }));
      setTimeSlots(updated);
      updateSelectedSlots(updated);
      return;
    }

    const indices     = timeSlots.map((s, i) => (s.selected ? i : -1)).filter((i) => i >= 0);
    const curStart    = indices[0];
    const curEnd      = indices[indices.length - 1];

    if (index >= curStart && index <= curEnd) {
      clearSelection();
      return;
    }

    const updated = applyDragRange(Math.min(curStart, index), Math.max(curEnd, index), timeSlots);
    setTimeSlots(updated);
    updateSelectedSlots(updated);
  };

  if (product?.capacity && searchCriteria?.people > product.capacity) return null;

  const clearSelection = () => {
    const updated = timeSlots.map((s) => ({ ...s, selected: false }));
    setTimeSlots(updated);
    setSelectedSlots([]);
    setShowMinimumInfo(false);
    setBookingError('');
    setHasUserSelected(true);
    
    setShowClearMessage(true);
    
    if (clearMessageTimeout.current) {
      clearTimeout(clearMessageTimeout.current);
    }
    
    clearMessageTimeout.current = setTimeout(() => {
      setShowClearMessage(false);
    }, 3000);
    
    if (onClearSelection && searchCriteria?.date) {
      onClearSelection(searchCriteria.date);
    }
  };

  const formatSelectedTimeRange = () => {
    if (selectedSlots.length === 0 && selectedTime?.start && selectedTime?.end && !hasUserSelected) {
      return `${formatTime12Hour(normaliseTime(selectedTime.start))} – ${formatTime12Hour(normaliseTime(selectedTime.end))}`;
    }
    if (selectedSlots.length === 0) return 'No time selected';
    const first = selectedSlots[0];
    const last  = selectedSlots[selectedSlots.length - 1];
    return `${formatTime12Hour(first.time)} – ${formatTime12Hour(last.endTime)}`;
  };

  const calculateTotalPrice = () => {
    try {
      if (selectedSlots.length > 0) {
        const hrs = selectedSlots.length * (slotIntervalInMinutes / 60);
        return ((product?.price?.priceAmt || 0) * hrs).toFixed(2);
      }
      if (selectedTime?.duration && !hasUserSelected) {
        return ((product?.price?.priceAmt || 0) * selectedTime.duration).toFixed(2);
      }
    } catch { /* ignore */ }
    return '0.00';
  };

  const getDurationLabel = () => {
    if (selectedSlots.length === 0 && selectedTime?.duration && !hasUserSelected) {
      const d = parseFloat(selectedTime.duration);
      return isNaN(d) ? '' : `${d} hour${d > 1 ? 's' : ''}`;
    }
    if (selectedSlots.length === 0) return '';
    const hrs = selectedSlots.length * (slotIntervalInMinutes / 60);
    return hrs === 1 ? '1 hour' : `${hrs} hours`;
  };

  const stripHtml = (html = '') => {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const formatDate = (d) => (d ? moment(d).format('MMM DD, YYYY') : 'Invalid date');

  const hasTimeConflicts = () => {
    if (!availabilityInfo?.conflictingSlots?.length) return false;
    if (!selectedTime?.start || !selectedTime?.end) return false;
    if (hasUserSelected) return false;
    const ns = normaliseTime(selectedTime.start);
    const ne = normaliseTime(selectedTime.end);
    return availabilityInfo.conflictingSlots.some(
      (c) => normaliseTime(c.start) === ns && normaliseTime(c.end) === ne,
    );
  };

  const timeConflict  = hasTimeConflicts();
  const requiredSlots = Math.ceil(60 / slotIntervalInMinutes);
  const isValidSelection = selectedSlots.length > 0 && validateMinimum(selectedSlots).isValid;
  const assignedProductId = availabilityInfo?.assignedProductId || availabilityInfo?.productData?.primaryId;
  const originalProductId = product?.originalPrimaryId;
  const isDifferentWorkspace = isAvailable
    && assignedProductId
    && originalProductId
    && String(assignedProductId) !== String(originalProductId);
  const assignedWorkspaceName = availabilityInfo?.productData?.name || 'another workspace';

  const handleSelectProduct = () => {
    if (product?.capacity && searchCriteria?.people > product.capacity) return;

    if (isEditing) {
      if (selectedSlots.length === 0) {
        setBookingError('Please select at least 1 hour.');
        setShowMinimumInfo(true);
        return;
      }
      const v = validateMinimum(selectedSlots);
      if (!v.isValid) {
        setBookingError(`Please select at least 1 hour. Select at least ${v.requiredSlots} slot(s).`);
        setShowMinimumInfo(true);
        return;
      }
    }

    if (!isPaymentAllowed && !isReviewMode) { setShowPaymentModal(true); return; }

    if (selectedSlots.length === 0) {
      if (!selectedTime?.start || !selectedTime?.end) {
        setBookingError('Please select a time slot.');
        return;
      }
      onSelect({ ...product, selectedTime, totalPrice: calculateTotalPrice() });
    } else {
      const first = selectedSlots[0];
      const last  = selectedSlots[selectedSlots.length - 1];
      onSelect({
        ...product,
        selectedTime: {
          start:       first.time,
          end:         last.endTime,
          duration:    selectedSlots.length * (slotIntervalInMinutes / 60),
          durationType:'HOUR',
        },
        totalPrice: calculateTotalPrice(),
      });
    }

    if (isEditing) {
      setShowUpdateSuccess(true);
      setTimeout(() => { setShowUpdateSuccess(false); setIsEditing(false); }, 2000);
    }
  };

  const renderDayOffBanner = () => {
    const dateMom  = dateInTz(searchCriteria?.date);
    const dayName  = dateMom.format('dddd');
    const isHoliday = holidays.some((h) => {
      const hDate = typeof h === 'string' ? h : h?.date;
      return hDate && moment(hDate).isSame(dateMom, 'day');
    });

    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
        <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertCircle className="w-7 h-7 text-amber-500" />
        </div>
        <p className="text-lg font-semibold text-slate-700">
          {isHoliday ? 'Holiday – Closed' : `${dayName} is not a working day`}
        </p>
        <p className="text-sm text-slate-500">
          No time slots are available for this date.
        </p>
      </div>
    );
  };

  const renderDesktopSlots = () => (
    <div
      ref={timelineRef}
      className="grid auto-cols-fr grid-flow-col gap-0 select-none"
      style={{ touchAction: 'none' }}
      onMouseLeave={() => { handleMouseUp(); handleMouseLeave(); }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchEnd}
    >
      {timeSlots.map((slot, index) => (
        <div key={index} data-slot-index={index} style={{ display: 'contents' }}>
          <TimeSlotItem
            slot={slot}
            index={index}
            isSelected={slot.selected}
            isBooked={slot.booked || slot.conflict}
            isPast={slot.past}
            isSelectable={slot.selectable}
            isHovered={hoveredSlot === index}
            isDragging={isDragging}
            handleMouseDown={handleMouseDown}
            handleMouseEnter={handleMouseEnter}
            handleMouseUp={handleMouseUp}
          />
        </div>
      ))}
    </div>
  );

  const renderMobileSlots = () => (
    <div className="flex flex-col divide-y divide-slate-100 select-none">
      {timeSlots.map((slot, index) => {
        let rowBg     = 'bg-white hover:bg-slate-50';
        let timeColor = 'text-slate-700';
        let badge     = <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Available</span>;

        if (slot.past) {
          rowBg     = 'bg-slate-50';
          timeColor = 'text-slate-400 line-through';
          badge     = <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Past</span>;
        } else if (slot.conflict) {
          rowBg     = 'bg-amber-50';
          timeColor = 'text-amber-600';
          badge     = <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">Unavailable</span>;
        } else if (slot.booked) {
          rowBg     = 'bg-red-50';
          timeColor = 'text-red-400';
          badge     = <span className="text-xs font-medium text-red-500 bg-red-100 px-2 py-0.5 rounded-full">Reserved</span>;
        } else if (slot.selected) {
          rowBg     = 'bg-blue-600 hover:bg-blue-700';
          timeColor = 'text-white font-semibold';
          badge     = <span className="text-xs font-medium text-blue-100 bg-blue-500 px-2 py-0.5 rounded-full">✓ Selected</span>;
        }

        return (
          <button
            key={index}
            type="button"
            data-slot-index={index}
            disabled={!slot.selectable || !isEditing}
            onClick={() => handleMobileTap(index)}
            className={`
              w-full flex items-center justify-between px-4 py-3.5
              transition-colors duration-150 text-left
              ${rowBg}
              ${!slot.selectable || !isEditing ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            `}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0
                transition-all duration-150
                ${slot.selected
                  ? 'border-white bg-white'
                  : slot.conflict
                    ? 'border-amber-400'
                    : slot.booked || slot.past
                      ? 'border-slate-300'
                      : 'border-blue-400'}
              `}>
                {slot.selected  && <div className="w-2.5 h-2.5 rounded-full bg-blue-600" />}
                {slot.conflict  && <AlertCircle className="w-3 h-3 text-amber-500" />}
              </div>
              <span className={`text-sm whitespace-nowrap ${timeColor}`}>
                {slot.displayLabel}
              </span>
            </div>
            <div className="shrink-0 ml-2">{badge}</div>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <div className={`
        bg-white border border-slate-200 rounded-xl shadow-md hover:shadow-lg
        transition-shadow duration-300 mb-4 sm:mb-6 overflow-visible relative w-full
        ${!isAvailable ? 'border-amber-300 bg-amber-50/30' : ''}
      `}>
        <ProductInfoModal
          showProductInfo={showProductInfo}
          setShowProductInfo={setShowProductInfo}
          product={product}
          companyState={companyState}
          stripHtml={stripHtml}
        />
        <MinimumDurationPopup
          showMinimumInfo={showMinimumInfo}
          setShowMinimumInfo={setShowMinimumInfo}
          requiredSlots={requiredSlots}
        />
        <PaymentAccessModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          contactPerson={contactPerson}
          companyName={companyState?.name}
        />

        <ProductHeader
          product={product}
          companyState={companyState}
          isExpanded={isCardExpanded}
          setIsExpanded={setIsCardExpanded}
          selectedSlots={selectedSlots}
          formatSelectedTimeRange={formatSelectedTimeRange}
          setShowProductInfo={setShowProductInfo}
          customRightElement={
            <div className="flex items-center gap-2">
              {totalOccurrences > 1 && (
                <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                  Session {occurrenceNumber} of {totalOccurrences}
                </span>
              )}
              {!isAvailable && (
                <span className="bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Conflict
                </span>
              )}
              {isDayOff && (
                <span className="bg-slate-100 text-slate-700 px-3 py-1.5 rounded-full text-xs font-semibold">
                  Day Off
                </span>
              )}
            </div>
          }
        />

        {isCardExpanded && (
          <div className="border-t border-slate-200 bg-slate-50">
            <div className="p-4 sm:p-5 lg:p-6">

              <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-3 mb-4 sm:mb-5">
                <DateInfoBox
                  searchCriteria={searchCriteria}
                  companyState={companyState}
                  customLabel={
                    <div className="flex items-center gap-2">
                      <span>{formatDate(searchCriteria?.date)}</span>
                      {totalOccurrences > 1 && (
                        <span className="bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full text-xs font-medium">
                          Session {occurrenceNumber}
                        </span>
                      )}
                    </div>
                  }
                />

                <div className="flex items-center gap-2 self-end xs:self-auto">
                  {isReviewMode && (
                    <>
                      <button
                        onClick={() => setIsEditing((v) => !v)}
                        className={`p-2 rounded-lg transition-colors duration-200 ${
                          isEditing
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                        }`}
                        title={isEditing ? 'Cancel editing' : 'Edit time'}
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors duration-200"
                        title="Delete occurrence"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {selectedSlots.length > 0 && isEditing && (
                    <ClearSelectionButton clearSelection={clearSelection} />
                  )}
                </div>
              </div>

              {isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center gap-2"
                >
                  <Edit3 className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    Editing mode – click and drag to select time slots
                  </span>
                </motion.div>
              )}

              {bookingError && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2"
                >
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-red-700">{bookingError}</span>
                </motion.div>
              )}

              {isDifferentWorkspace && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                  <span className="text-sm font-medium text-blue-700">
                    This session is assigned to {assignedWorkspaceName} in the same category and price group.
                  </span>
                </div>
              )}

              {isDayOff ? (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  {renderDayOffBanner()}
                </div>
              ) : (
                <>
                  <div className="block lg:hidden bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                      <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                        {isEditing ? 'Tap to select · tap again to extend' : 'Pre-selected time'}
                      </span>
                      {selectedSlots.length > 0 && (
                        <span className="text-xs font-bold text-blue-600">
                          {selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} selected
                        </span>
                      )}
                    </div>
                    {renderMobileSlots()}
                  </div>

                  <div className="hidden lg:block bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                    {renderDesktopSlots()}
                  </div>

                  <TimeSlotLegend showConflict={timeConflict} />

                  {(selectedSlots.length > 0 || selectedTime) && (
                    <SelectedTimeSummary
                      formatSelectedTimeRange={formatSelectedTimeRange}
                      getDurationLabel={getDurationLabel}
                      product={product}
                      calculateTotalPrice={calculateTotalPrice}
                      showMinimumInfo={showMinimumInfo}
                      handleSelectProduct={handleSelectProduct}
                      customButtonText={
                        isEditing
                          ? timeConflict
                            ? 'Time Not Available'
                            : selectedSlots.length === 0
                              ? 'Select Time First'
                              : isValidSelection
                                ? 'Update Selection'
                                : 'Minimum 1 Hour Required'
                          : selectedSlots.length === 0 && (!selectedTime?.start || hasUserSelected)
                            ? 'Select Time First'
                            : timeConflict
                              ? 'Time Not Available'
                              : 'Confirm Selection'
                      }
                      disabled={timeConflict || (selectedSlots.length === 0 && (!selectedTime?.start || hasUserSelected)) || (selectedSlots.length > 0 && !isValidSelection)}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showClearMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.9 }}
            className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-amber-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5" />
            <span className="font-semibold">Selection cleared! Please select a new time slot.</span>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Delete Session?</h3>
                  <p className="text-slate-600 text-sm">
                    Remove the booking on{' '}
                    <span className="font-semibold text-slate-900">{formatDate(searchCriteria?.date)}</span>?
                    This cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-6 py-3 border-2 border-slate-300 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 transition-all"
                >Cancel</motion.button>
                <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    if (onDelete) onDelete(searchCriteria?.date, occurrenceNumber - 1);
                    setShowDeleteConfirm(false);
                  }}
                  className="flex-1 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-all shadow-lg shadow-red-200"
                >Delete</motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showUpdateSuccess && (
          <motion.div
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-8 right-8 bg-emerald-500 text-white px-6 py-4 rounded-xl shadow-2xl z-50 flex items-center gap-3"
          >
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Time updated successfully!</span>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default RecurrenceProductResultCard;
